import { FC, useState } from "react";
import classNames from "classnames/bind";
import { Flex, Heading, Box } from "@smile2impress/components";
import { useRouter } from "next/router";
import { SYTLegacyTheme } from "@smile2impress/themes";
import Head from "next/head";
import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import { TagFragmentFragment } from "api/cms-website/generated/graphql";

import { UTSLocaleToLanguage } from "constants/enum";
import { Container, ContainerSize } from "components/Container";
import { Sharing, SharingLanguage } from "components/Sharing";
import { formatDate } from "utils/date";
import { TableOfContents } from "components/TableOfContents";
import { StickyBookButton } from "components/StickyBookButton/StickyBookButton";
import { BlogHreflangService } from "services/BlogHreflangService";
import {
    generateJSONSchema,
    JSONSchemaServiceContentType,
} from "services/JSONSchemaService";
import { UrlsConfig } from "configs/urlsConfig";
import { NestedThemeProvider } from "components/NestedThemeProvider";
import { cleanHtml } from "utils/cleanHtml";
import { Questions } from "components/Relaunch/Questions";

import { BlogPostProps } from "./BlogPostLayout.type";
import styles from "./BlogPostLayout.module.scss";
import useBlogTags from "./utils/useBlogTags";
import { blocks } from "./blocks";

const cx = classNames.bind(styles);

export const BlogPostLayout: FC<BlogPostProps> = ({ content, post }) => {
    const { locale, shareTitle } = content;
    const language = UTSLocaleToLanguage[locale];
    const {
        cover: coverImage,
        name: title,
        publication_date: date,
        content: postContent,
        content_preview: contentPreview,
        seo,
        tags,
        tableOfContents,
        readingTime,
        localizations,
        faq,
    } = post;

    const { metaTitle: seoTitle, metaDescription: seoDescription } = seo || {};

    const { asPath, locale: routerLocale } = useRouter();

    const [sharingLink] = useState(
        `${UrlsConfig.HOME_PAGE_URL}/${routerLocale}${asPath}`
    );

    const formattedDate = formatDate(new Date(date), "dd MMMM", language);

    const { href = "", text = "" } = useBlogTags(
        tags as TagFragmentFragment[],
        content,
        locale
    );

    const metaImageUrl = coverImage?.desktop?.url || coverImage?.mobile?.url;
    const schema = generateJSONSchema({
        type: JSONSchemaServiceContentType.BLOG,
        content: {
            datePublished: date,
            imageUrl: metaImageUrl,
            title: seoTitle || title,
            description: cleanHtml(seoDescription || contentPreview),
            pageUrl: sharingLink,
        },
    });

    return (
        <>
            <Head>
                <meta charSet="utf-8" />
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1.0"
                />
                <meta name="keywords" content="Impress" />
                <meta property="og:url" content={sharingLink} />
                <meta property="og:type" content="article" />
                <meta property="og:title" content={cleanHtml(title)} />
                <meta
                    property="og:description"
                    content={cleanHtml(contentPreview)}
                />
                {metaImageUrl && (
                    <>
                        <meta property="og:image" content={metaImageUrl} />
                        <meta name="twitter:image" content={metaImageUrl} />
                    </>
                )}
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={contentPreview} />
                <meta name="twitter:card" content="summary_large_image" />
                <title key="metaTitle">{seoTitle || title}</title>
                <meta
                    name="description"
                    key="metaDescription"
                    content={seoDescription}
                />
                {schema && (
                    <script
                        key="JSONSchema"
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{
                            __html: JSON.stringify(schema).replace(
                                /</g,
                                "\\u003c"
                            ),
                        }}
                    />
                )}
            </Head>
            <BlogHreflangService hreflangs={localizations} />
            <NestedThemeProvider theme={SYTLegacyTheme}>
                <main style={{ position: "relative" }}>
                    <Container className={cx("main")} size={ContainerSize.FULL}>
                        <div className={cx("main__info", "info")}>
                            <Heading as="h1" textStyle="display2" mb={[2, 3]}>
                                {title}
                            </Heading>
                            <div className={cx("info__meta")}>
                                {formattedDate}&nbsp;·&nbsp;{readingTime}
                            </div>
                        </div>
                        <div
                            className={cx("main__cover")}
                            style={{
                                backgroundImage: `url(${metaImageUrl})`,
                            }}
                        ></div>
                    </Container>

                    <Container className={cx("content")}>
                        <div className={cx("blocks")}>
                            <BlocksRenderer
                                content={postContent}
                                blocks={blocks}
                            />
                        </div>
                        <TableOfContents
                            className={cx("menu")}
                            headings={tableOfContents}
                        />
                    </Container>
                    <Container>
                        <div className={cx("share")}>
                            <span className={cx("share__title")}>
                                {shareTitle}
                            </span>
                            <Sharing
                                language={language as SharingLanguage}
                                link={sharingLink}
                                title={title}
                                description={contentPreview}
                            />
                        </div>
                    </Container>
                    {faq && (
                        <Container>
                            <Box
                                borderTop="1px solid"
                                borderColor="line.secondary"
                                mb="40px"
                            ></Box>
                            <Box mx={[0, 0, -4]}>
                                <Questions {...faq} />
                            </Box>
                        </Container>
                    )}
                </main>
                {text && (
                    <Flex
                        position={{
                            base: "sticky",
                            desktop: "fixed",
                        }}
                        direction="row"
                        bottom={2}
                        right={0}
                        marginRight={3}
                        marginLeft={[3, 3, 0]}
                        gap={1}
                        justifyContent={"flex-end"}
                        mixBlendMode="normal"
                        zIndex="docked"
                    >
                        <StickyBookButton href={href}>{text}</StickyBookButton>
                    </Flex>
                )}
            </NestedThemeProvider>
        </>
    );
};
